import { c as _c } from "react/compiler-runtime";
import { getErrorCode, postHostedOnboardingOptions } from '@onefootprint/fetch-hosted';
import type { ClickedButton, RequirementConfirmations } from '@onefootprint/request-types';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import z from 'zod';
import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import { setIdv2StoreData } from '@/idv2/features/idv-to-idv2';
import { getLogger } from '@/idv2/utils/logger';
import nid from '@/idv2/utils/neuro-id';
import { getProceedQueryKey, getStableBackOptions, getStableProceedOptions } from '@/idv2/utils/proceed-query';
import { getRouteForRequirement, handleLayoutRoute, isLayoutRoute } from '@/idv2/utils/route-helpers';
import { suppressNextStuckDetection, trackRequirementTransition } from '@/idv2/utils/route-tracking';

/** Search params for onboarding navigation */
const onboardingSearchSchema = z.object({
  confirmations: z.custom<RequirementConfirmations>().optional(),
  chosenSuccessorId: z.string().optional(),
  clickedButton: z.object({
    nodeId: z.string(),
    index: z.number()
  }).optional()
});
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'onboarding-token-route'
});

/** Redirect to the layout route when the child route doesn't match the current requirement (e.g. after legacy IDV handoff). */
const redirectIfStaleChildRoute = (pathname: string, requirementKind: string, token: string) => {
  // Transfer is an intermediate route before liveness/document requirements, not a direct
  // requirement-to-route mapping. It has its own validation in its beforeLoad.
  if (pathname.endsWith('/transfer')) return;
  const expectedRoute = getRouteForRequirement(requirementKind as Parameters<typeof getRouteForRequirement>[0]);
  const expectedSegment = expectedRoute?.split('/').pop();
  if (!expectedRoute || !expectedSegment || !pathname.includes(`/${expectedSegment}`)) {
    logWarn('Stale child route detected, redirecting to layout route', {
      currentPath: pathname.replace(token, '$token'),
      requirementKind,
      expectedRoute: expectedRoute ?? 'none'
    });
    throw redirect({
      to: '/onboarding/$token',
      params: {
        token
      }
    });
  }
};
const retryOn137 = (failureCount: number, error: Error) => getErrorCode(error) === 'E137' && failureCount <= 2;

/**
 * Fetches the next requirement, or the previous one when going back, automatically handling sleep
 * requirements by waiting and refetching until a non-sleep requirement (or completion) is returned.
 * A sleep means "wait, then ask again" whichever call returned it, so going back waits it out too
 * instead of reaching the router, which has no screen for it.
 *
 * Sleep waits are wrapped in queryClient.fetchQuery so they count as active queries,
 * keeping useIsLoading() true and the loading spinner visible during the wait.
 */
const fetchNextRequirement = async ({
  queryClient,
  headers,
  chosenSuccessorId,
  clickedButton,
  confirmations,
  navigation
}: {
  queryClient: QueryClient;
  headers: {
    'X-Fp-Authorization': string;
  };
  chosenSuccessorId?: string;
  clickedButton?: ClickedButton;
  confirmations?: RequirementConfirmations;
  navigation?: 'forward' | 'back';
}) => {
  const proceedOptions = getStableProceedOptions({
    headers,
    body: {
      chosenSuccessorId,
      clickedButton,
      confirmations
    }
  });
  const getRequirement = () => queryClient.fetchQuery({
    ...proceedOptions,
    staleTime: Infinity,
    retry: retryOn137
  });
  const backOptions = getStableBackOptions({
    headers
  });
  const goBack = () => queryClient.fetchQuery({
    ...backOptions,
    // A finished workflow (E144) or one with no step behind (E146) refuses Back: show where it is now. Inside queryFn
    // so the answer is cached under the back key, since the layout redirect re-runs this and would POST Back again.
    queryFn: context => backOptions.queryFn(context).catch(error => {
      if (!['E144', 'E146'].includes(getErrorCode(error) ?? '')) throw error;
      suppressNextStuckDetection();
      return proceedOptions.queryFn(context);
    }),
    staleTime: Infinity,
    retry: retryOn137
  });

  // Handle sleep requirements inline to avoid flickering from rendering a separate component.
  // Wait for the specified period, then refetch until we get a non-sleep requirement.
  let result = navigation === 'back' ? await goBack() : await getRequirement();
  while (result.kind !== 'complete') {
    const firstReq = result.allRequirements[0];
    if (firstReq?.kind !== 'sleep') break;
    logInfo(`Sleep requirement: waiting ${firstReq.periodSecs}s`, {
      periodSecs: firstReq.periodSecs
    });
    // Use fetchQuery so the wait registers as an active query, keeping the loading spinner visible.
    await queryClient.fetchQuery({
      queryKey: ['sleep-wait', Date.now()],
      queryFn: () => new Promise(resolve => setTimeout(() => resolve(true), firstReq.periodSecs * 1000)),
      staleTime: 0
    });
    await queryClient.invalidateQueries({
      type: 'all',
      queryKey: getProceedQueryKey(),
      refetchType: 'none'
    });
    result = await getRequirement();
  }
  return result;
};
export const Route = createFileRoute('/onboarding/$token')({
  validateSearch: onboardingSearchSchema,
  beforeLoad: async ({
    context: {
      queryClient,
      overallOutcome,
      fixtureRiskSignals,
      config,
      navigation
    },
    params: {
      token
    },
    search: {
      confirmations,
      chosenSuccessorId,
      clickedButton
    },
    location
  }) => {
    const headers = {
      'X-Fp-Authorization': token
    };
    const isLayout = isLayoutRoute(location.pathname, token);

    // Initialize onboarding session. Each POST writes the bootstrap data over the user's edits, so keep
    // the result past the default 5-minute gcTime rather than POSTing again on a later step.
    await queryClient.ensureQueryData({
      ...postHostedOnboardingOptions({
        headers,
        body: {
          fixtureResult: overallOutcome,
          fixtureRiskSignals
        }
      }),
      gcTime: Infinity,
      retry: retryOnConnectionError
    });
    nid.identify(token, queryClient);

    // Fetch next requirement, automatically waiting through any sleep requirements.
    // Counter-intuitively, we use `staleTime: Infinity` here and manually invalidate the query cache when we want to re-fetch requirements
    const nextRequirement = await fetchNextRequirement({
      queryClient,
      headers,
      chosenSuccessorId,
      clickedButton,
      confirmations,
      navigation
    });

    // Handle onboarding completion
    if (nextRequirement.kind === 'complete') {
      logInfo('Onboarding complete');
      await setIdv2StoreData({
        completionPage: nextRequirement.page ?? undefined
      });
      throw redirect({
        to: '/complete',
        search: {
          validationToken: nextRequirement.validationToken
        }
      });
    }
    const currentRequirement = nextRequirement.allRequirements[0];
    const allRequirements = nextRequirement.allRequirements;
    const canGoBack = nextRequirement.canGoBack;
    logInfo(`Requirement determined: ${currentRequirement.kind}`, {
      canGoBack,
      navigation: navigation ?? 'forward',
      path: location.pathname.replace(token, '$token'),
      requirementKind: currentRequirement.kind
    });

    // Update shared state for legacy IDV
    await setIdv2StoreData({
      canGoBack,
      allRequirements,
      requirementPage: undefined,
      completionPage: undefined
    });

    // Layout route: redirect to appropriate child
    if (isLayout) {
      await handleLayoutRoute(currentRequirement, token, config, {
        allRequirements,
        confirmations
      });
    } else {
      redirectIfStaleChildRoute(location.pathname, currentRequirement.kind, token);
      trackRequirementTransition(currentRequirement, confirmations, location.pathname.replace(token, '$token'));
    }
    return {
      currentRequirement,
      canGoBack,
      allRequirements,
      confirmations,
      navigation,
      requiredConsent: nextRequirement.requiredConsent
    };
  },
  loader: ({
    context: {
      currentRequirement
    }
  }) => ({
    currentRequirement
  }),
  component: OnboardingLayout
});
function OnboardingLayout() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Outlet />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}